/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/category-list/table.tsx
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import { Link, useLoaderData, useOutletContext, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import { PiPlusBold as Plus } from "react-icons/pi";
import { Alert, Button } from "rizzui";
import { EmptyState } from "@steelyard/theme";
import { PagedTable } from "../../components/table/data-table";
import { ListHeader } from "../../components/table/list-header";
import { activeStatusOptions, TableSearch } from "../../components/table/toolbar";
import { tierColumns } from "./columns";
import { PricingFlow } from "../../components/pricing-flow";
import { CATALOG_MAX_PAGE_SIZE, listTiers, loadActiveView, updateTier, type Tier } from "../../lib/services/catalog";
import { can } from "../../lib/permissions";
import { activeViewParams, hrefWith, readActiveView, readPaging } from "../../lib/paging";
import type { PermissionState, AccountSummary } from "../../types";

/**
 * T059. Same pattern as `catalogue/brands/List.tsx`: loader reads page and the All / Active /
 * Inactive view from the URL so paging and filtering stay bookmarkable, `PagedTable` owns the
 * frame, and a deactivated tier stays visible and badged rather than disappearing (DELETE
 * deactivates, never removes, same as brand/category/product). `loadActiveView` reads the view and
 * every count, the same way brands does.
 */
export async function tiersListLoader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const view = readActiveView(url.searchParams);
  const loaded = await loadActiveView(
    view,
    ({ page, pageSize, activeOnly }) => listTiers({ page, pageSize, activeOnly: activeOnly || undefined }),
    readPaging(url.searchParams),
  );
  return { ...loaded, view };
}

export default function TiersList() {
  const { result, view, complete, counts } = useLoaderData() as Awaited<ReturnType<typeof tiersListLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams] = useSearchParams();
  const canWrite = can(permissionState, "pricing.administer");

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Tiers"
        count={result.totalCount}
        description="Price lists for groups of customers. Each customer is on one tier; guests and anyone without one pay the Default Tier's prices — the base prices."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Pricing" }, { name: "Tiers" }]}
      />

      <PricingFlow />

      {!complete ? (
        <Alert color="warning" variant="flat">
          Inactive shows the inactive tiers among the first {CATALOG_MAX_PAGE_SIZE} of {counts.all}. The catalogue service
          has no inactive-only filter yet, so any beyond that are not listed here.
        </Alert>
      ) : null}

      <PagedTable<Tier>
        actions={
          canWrite ? (
            <Link to="/tiers/new">
              <Button as="span">
                <Plus aria-hidden className="me-1.5 size-4" />
                New tier
              </Button>
            </Link>
          ) : null
        }
        result={result}
        caption="Tiers, with purchase types, rounding mode and effective date"
        columns={tierColumns(
          canWrite
            ? // No deactivate route for a tier: the status rides PUT /tiers/{id} with the row's own values.
              ({ id, name, allowPiecePurchase, allowUnitPurchase, priceRoundingMode, effectiveFrom }, isActive) =>
                updateTier(id, { name, allowPiecePurchase, allowUnitPurchase, priceRoundingMode, effectiveFrom, isActive })
            : undefined,
        )}
        getRowId={(row) => row.id}
        status={{
          label: "Status",
          options: activeStatusOptions(view, (next) => hrefWith("/tiers", searchParams, activeViewParams(next)), counts),
        }}
        // The tier route takes no search parameter, so this searches the rows already loaded.
        search={(table) => <TableSearch table={table} placeholder={view === "inactive" ? "Search inactive tiers…" : "Search this page…"} />}
        clear={{ active: view !== "active", href: hrefWith("/tiers", searchParams, activeViewParams("active")) }}
        empty={
          <EmptyState
            title={view === "active" ? "No active tiers" : view === "inactive" ? "No inactive tiers" : "No tiers yet"}
            action={
              canWrite && view === "all" ? (
                <Link to="/tiers/new">
                  <Button as="span" size="sm">
                    <Plus aria-hidden />
                    New tier
                  </Button>
                </Link>
              ) : view !== "all" ? (
                <Link to="/tiers?all=1">
                  <Button as="span" variant="outline" size="sm">
                    Show every tier
                  </Button>
                </Link>
              ) : null
            }
          >
            {view === "active"
              ? "No tier is active right now — every one has been deactivated."
              : view === "inactive"
                ? "Every tier is active. A deactivated one stays listed here, never removed."
                : "Create the first one to start assigning customers to B2B pricing."}
          </EmptyState>
        }
      />
    </div>
  );
}
